import { useEffect, useRef, useState } from "react";
import type { MediaStreamLike } from "@cuekards/ui";

/**
 * How loud the room is, for the six bars under the self-view (web-ui ticket 05).
 *
 * **Why this observes the preview rather than reusing the microphone fork.**
 * `audio-tap.ts` already forks the microphone for the recogniser, and the
 * obvious move is to read a level off that. It is the wrong one, because since
 * ticket 04 the engine holds "the microphone is open" and "the microphone is
 * tapped" as two separate facts: a preview opens the device with
 * `Standby.untap` null, and the fork is only attached when rehearsal is wanted.
 * Reading the tap would therefore mean turning rehearsal on to draw a meter —
 * starting the recogniser and entering a mode the visitor did not ask for —
 * or threading a display concern down into `CaptureEnginePorts`, which holds
 * hardware and no decisions.
 *
 * Observing the open track instead is the same class of thing the tap itself is,
 * and `audio-tap.ts` already makes the argument for it: it does not clone the
 * stream, re-open the device, or sit between the track and the recorder. FR-31's
 * invariant is that the microphone is *opened* exactly once, and a
 * `MediaStreamAudioSourceNode` over an already-open track is not an open.
 */

/** How far a level has to move before it is worth a render. */
const WORTH_A_RENDER = 0.02;

/** At most this often, so a pointer-free page still costs the compositor
 * nothing while a camera is live behind it. */
const PUBLISH_EVERY_MS = 66;

/**
 * How fast a peak falls back, per frame.
 *
 * A meter driven by raw RMS strobes: speech is a train of bursts and gaps at a
 * rate the eye resolves, so the bars flicker to nothing between syllables and
 * the reading is unreadable. Holding the peak and letting it decay is what makes
 * it look like a level rather than a fault.
 */
const DECAY = 0.92;

/**
 * The curve from amplitude to bars.
 *
 * Linear is wrong in the direction that matters: speech sits around an RMS of
 * 0.05–0.3, so a linear six-bar meter never leaves its first bar and answers
 * "is the microphone hearing me" with a permanent maybe. The square root opens
 * the bottom of the range out, which is the half anyone actually looks at, and
 * still leaves headroom above a normal speaking voice.
 */
const CURVE = 0.5;

/**
 * How many of `count` bars are lit at this level. Pure, so the ramp is a test
 * rather than something judged by looking at it.
 *
 * `null` is not silence — it is a microphone that is off, absent or held by
 * something else — and both rest at zero bars, because there is nothing useful
 * to say with six spans about the difference.
 */
export function meterBars(level: number | null, count: number): number {
  if (level === null || !Number.isFinite(level) || level <= 0) return 0;
  const clamped = Math.min(1, level);
  return Math.min(count, Math.ceil(count * Math.pow(clamped, CURVE)));
}

/** The live level of an open stream's audio, or null when there is none to
 * hear. */
export function useMicLevel(stream: MediaStreamLike | null): number | null {
  const [level, setLevel] = useState<number | null>(null);
  // Read inside the loop rather than as a dependency: a level that changed on
  // every frame would rebuild the audio graph on every frame.
  const published = useRef<number | null>(null);
  /**
   * The **track**, and it is what the effect below is keyed on (web-ui ticket 06).
   *
   * `createMediaStreamSource` reads the stream's audio track once, at
   * construction, and does not follow one being swapped afterwards — and since
   * ticket 06 a microphone switched off and back on is exactly that: a new track
   * grafted into the stream the page already holds, whose identity deliberately
   * never moves so the `<video>` beside this does not re-bind. Keyed on the
   * stream alone, the graph would go on reading a track that had been stopped,
   * and the meter would rest at nothing for the rest of the visit with a live
   * microphone in front of it.
   */
  const track =
    stream?.getTracks().find((candidate) => candidate.kind === "audio") ?? null;

  useEffect(() => {
    if (stream === null || track === null) {
      published.current = null;
      setLevel(null);
      return;
    }

    let stopped = false;
    let frame = 0;
    let peak = 0;
    let lastPublishedAt = 0;

    // Guarded as a whole. Every step here is a browser API that is entitled to
    // refuse — a context the autoplay policy will not start, a track that ends
    // between the check above and the connection — and none of them is worth
    // taking the green room down for. A meter that does not move is a small
    // loss; a room that does not render is the product.
    let context: AudioContext | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    let analyser: AnalyserNode | null = null;
    try {
      context = new AudioContext();
      // Constructed without a user gesture on a return visit, where the camera
      // comes up silently, so it can start suspended. Chromium treats granted
      // media permission as enough to resume; where it does not, the bars simply
      // rest and nothing else notices.
      void context.resume().catch(() => {});
      source = context.createMediaStreamSource(stream as MediaStream);
      analyser = context.createAnalyser();
      analyser.fftSize = 512;
      // Deliberately not connected onward to the destination. This is a read;
      // routing the microphone to the speakers would be feedback in front of a
      // camera.
      source.connect(analyser);
    } catch {
      /* The context was built before the failure — `createMediaStreamSource` is
       * what throws, on the ended track the comment above describes — and a
       * context nobody closes keeps its claim on the audio hardware for the life
       * of the tab. Chromium caps how many one document may hold, so a few device
       * toggles through this path would leave `new AudioContext()` itself
       * throwing and the meter dead for the rest of the visit. */
      void context?.close().catch(() => {});
      published.current = null;
      setLevel(null);
      return;
    }

    const samples = new Float32Array(analyser.fftSize);
    const node = analyser;

    const read = (): void => {
      if (stopped) return;
      frame = requestAnimationFrame(read);
      node.getFloatTimeDomainData(samples);
      let sum = 0;
      for (const sample of samples) sum += sample * sample;
      const rms = Math.sqrt(sum / samples.length);
      peak = Math.max(rms, peak * DECAY);

      const now = performance.now();
      if (now - lastPublishedAt < PUBLISH_EVERY_MS) return;
      const previous = published.current;
      if (previous !== null && Math.abs(previous - peak) < WORTH_A_RENDER) return;
      lastPublishedAt = now;
      published.current = peak;
      setLevel(peak);
    };
    frame = requestAnimationFrame(read);

    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
      // Idempotent and unguarded individually, the way the tap's teardown is: a
      // context left running holds the audio hardware awake for the life of the
      // tab, and this runs on every device toggle.
      try {
        source?.disconnect();
        node.disconnect();
        void context?.close().catch(() => {});
      } catch {
        // Already gone.
      }
      published.current = null;
      setLevel(null);
    };
  }, [stream, track]);

  return level;
}
